import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const AUTH = {
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
  },
};

function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

// Navbar dropdowns
  const [openDropdown, setOpenDropdown] = useState(null); // "teams" | "leaders" | "players" | null
  const teamsRef = useRef(null);
  const leadersRef = useRef(null);
  const playersRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null); // "teams" | "leaders" | "players" | null
  
  // Navbar search
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navbarSearchTerm, setNavbarSearchTerm] = useState("");
  const [navbarResults, setNavbarResults] = useState([]);
  const [navbarSelectedIndex, setNavbarSelectedIndex] = useState(-1);
  const navbarInputRef = useRef(null);
  const navbarContainerRef = useRef(null);

// Close search + any dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (e) => {
    // Close expanded search
    if (
      navbarContainerRef.current &&
      !navbarContainerRef.current.contains(e.target)
    ) {
      setSearchExpanded(false);
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }

    // Close dropdowns
    if (
      teamsRef.current && !teamsRef.current.contains(e.target) &&
      leadersRef.current && !leadersRef.current.contains(e.target) &&
      playersRef.current && !playersRef.current.contains(e.target)
    ) {
      setOpenDropdown(null);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  // ========== Navbar Search ==========
  useEffect(() => {
    if (!navbarSearchTerm.trim()) {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
      return;
    }

    const search = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(
            navbarSearchTerm
          )}`,
          AUTH
        );
        setNavbarResults(res.data.slice(0, 12));
        setNavbarSelectedIndex(-1);
      } catch (err) {
        console.error("Navbar search error:", err);
      }
    };

    const timeout = setTimeout(search, 400);
    return () => clearTimeout(timeout);
  }, [navbarSearchTerm]);

  // Close navbar results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        navbarContainerRef.current &&
        !navbarContainerRef.current.contains(e.target)
      ) {
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

// Keyboard navigation – Navbar
useEffect(() => {
  const handleKeyDown = (e) => {
    if (!navbarResults.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) =>
        Math.min(prev + 1, navbarResults.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && navbarSelectedIndex >= 0) {
      const selected = navbarResults[navbarSelectedIndex];
      const path =
        selected.type === "team"
          ? `/team/${selected.abbreviation}`
          : `/player/${selected.bdl_id}`;
      window.location.href = path;
      setNavbarSearchTerm("");
      setNavbarResults([]);
    } else if (e.key === "Escape") {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }
  };

  document.addEventListener("keydown", handleKeyDown);
  return () => document.removeEventListener("keydown", handleKeyDown);
}, [navbarResults, navbarSelectedIndex]);


  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-teams`,
          AUTH
        );
        setTeams(res.data);
      } catch (err) {
        console.error("Teams fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-3xl">
        Loading Teams...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans">
      {/* NAVBAR */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-red-500/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/HOFLogoSquare.png"
              alt="HOF Logo"
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="text-2xl font-black tracking-tighter">
                HELMETS OFF
              </div>
              <div className="text-[10px] text-red-400 -mt-1">FOOTBALL</div>
            </div>
          </Link>

          <div className="flex-1 hidden md:block"></div>

{/* Right side */}
<div className="flex items-center gap-4">
  
  {/* Collapsible Search (works on all screen sizes) */}
  <div className="relative" ref={navbarContainerRef}>
    {!searchExpanded ? (
      <button
        onClick={() => {
          setSearchExpanded(true);
          setTimeout(() => navbarInputRef.current?.focus(), 50);
        }}
        className="p-2 text-zinc-400 hover:text-white transition"
        aria-label="Search"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>
    ) : (
      <div className="relative w-64 sm:w-72">
        <input
          ref={navbarInputRef}
          type="text"
          value={navbarSearchTerm}
          onChange={(e) => setNavbarSearchTerm(e.target.value)}
          placeholder="Search players..."
          className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-3 px-5 pl-12 text-sm focus:outline-none focus:border-red-500"
          autoFocus
        />
        <span className="absolute left-4 top-3.5 text-zinc-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>

        {navbarResults.length > 0 && (
          <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
            {navbarResults.map((item, index) => (
              <Link
                key={`${item.type}-${item.bdl_id}`}
                to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id}`}
                className={`block px-4 py-3 hover:bg-zinc-800 ${navbarSelectedIndex === index ? "bg-zinc-800" : ""}`}
                onClick={() => {
                  setNavbarSearchTerm("");
                  setNavbarResults([]);
                  setSearchExpanded(false);
                }}
              >
                {item.type === "team" ? (
                  <>
                    <span className="font-medium">{item.name}</span>
                    <span className="text-zinc-500 text-sm"> • Team</span>
                  </>
                ) : (
                  <>
                    {item.first_name} {item.last_name} • {item.position_abbreviation}
                  </>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    )}
  </div>

  {/* Desktop Nav Links */}
  <div className="hidden md:flex items-center gap-8 text-sm font-medium">
    {/* TEAMS */}
    <div className="relative" ref={teamsRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "teams" ? null : "teams")}
        className="hover:text-red-400 transition"
      >
        TEAMS
      </button>
      {openDropdown === "teams" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/teams" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>All Teams</Link>
          <Link to="/Standigs" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Standings</Link>
          <Link to="/Schedule" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Schedule</Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="relative" ref={leadersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "leaders" ? null : "leaders")}
        className="hover:text-red-400 transition"
      >
        LEADERS
      </button>
      {openDropdown === "leaders" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/leaderboards" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Season Leaders</Link>
          <Link to="/leaderboards?stat=passing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Passing</Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Rushing</Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Receiving</Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div className="relative" ref={playersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "players" ? null : "players")}
        className="hover:text-red-400 transition"
      >
        PLAYERS
      </button>
      {openDropdown === "players" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/playerStats" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Player Stats</Link>
          <Link to="/playerCompare" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player compare</Link>
        </div>
      )}
    </div>
  </div>

  {/* Mobile Hamburger Button */}
  <button
    className="md:hidden p-2 text-zinc-400 hover:text-white"
    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
    aria-label="Menu"
  >
    {mobileMenuOpen ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    )}
  </button>
</div>

        </div>

{/* Mobile Menu - collapsible sections */}
{mobileMenuOpen && (
  <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md border-b border-zinc-800 px-6 py-5 z-40">
    
    {/* TEAMS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "teams" ? null : "teams")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        TEAMS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "teams" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "teams" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/teams" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            All Teams
          </Link>
          <Link to="/Standings" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Standings
          </Link>
          <Link to="/Schedule" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Schedule
          </Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "leaders" ? null : "leaders")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        LEADERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "leaders" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "leaders" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/leaderboards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Season Leaders
          </Link>
          <Link to="/leaderboards?stat=passing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Passing
          </Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Rushing
          </Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Receiving
          </Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div>
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "players" ? null : "players")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        PLAYERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "players" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "players" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/playerStats" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Stats
          </Link>
          <Link to="/playerCompare" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Comparison
          </Link>
         </div>
      )}
    </div>

  </div>
)}
      </nav>

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-6xl md:text-7xl font-black text-center mb-16 tracking-tighter">
            ALL TEAMS
          </h1>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
            {teams.map((team) => (
              <Link
                key={team.id || team.bdl_id}
                to={`/team/${team.abbreviation}`}
                className="group bg-zinc-900 border border-zinc-700 hover:border-red-500 rounded-3xl p-8 text-center transition-all hover:scale-105 hover:shadow-2xl"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-red-500 via-white to-blue-500 rounded-2xl mx-auto mb-6 flex items-center justify-center text-5xl font-black border-2 border-white/30 shadow-inner">
                  {team.abbreviation}
                </div>
                <h3 className="font-bold text-xl group-hover:text-red-400 transition">
                  {team.full_name || team.name}
                </h3>
                <p className="text-sm text-zinc-500 mt-1">
                  {team.conference} {team.division}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* FOOTER - Same as other pages */}
      <footer className="bg-black border-t border-zinc-800 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center text-zinc-500">
          <p>© 2026 Helmets Off Football • Serratox</p>
        </div>
      </footer>
    </div>
  );
}

export default TeamsPage;
